import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { payoutService } from "@/services";
import type { PayoutView } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { StatCard } from "@/components/base/StatCard";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDate, formatMonthYear } from "@/utils/format";
import { payoutStatusMeta } from "./components/labels";

function startOfMonth(): Date {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), 1);
}

interface Grouped {
  awaiting: PayoutView[];
  approved: PayoutView[];
  completed: PayoutView[];
}

function groupPayouts(payouts: PayoutView[]): Grouped {
  const g: Grouped = { awaiting: [], approved: [], completed: [] };
  for (const p of payouts) {
    if (p.status === "pending_approval") g.awaiting.push(p);
    else if (p.status === "approved") g.approved.push(p);
    else g.completed.push(p);
  }
  return g;
}

/** Funding progress for an actionable payout, or a neutral read for finished ones. */
function FundingCell({ payout }: { payout: PayoutView }) {
  const actionable = payout.status === "pending_approval" || payout.status === "approved";
  const pct = payout.expectedCollection > 0 ? Math.round((payout.collected / payout.expectedCollection) * 100) : 0;
  const short = actionable && payout.fundingShortfall > 0;
  return (
    <div className="flex items-center gap-2">
      {short ? (
        <i className="ri-alarm-warning-line text-base text-warning-600" title="Payout exceeds collected funds" />
      ) : (
        <i className="ri-wallet-3-line text-base text-secondary-500" />
      )}
      <div>
        <div className="h-1.5 w-24 overflow-hidden rounded-full bg-background-200">
          <div
            className={`h-full rounded-full ${short ? "bg-warning-500" : "bg-accent-500"}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <p className={`tnum mt-1 whitespace-nowrap text-xs ${short ? "font-medium text-warning-700" : "text-foreground-500"}`}>
          {short ? `Short ${payout.fundingShortfall.toLocaleString("en-IN")}` : `${pct}% funded`}
        </p>
      </div>
    </div>
  );
}

function PayoutEntry({ payout, onOpen }: { payout: PayoutView; onOpen: (p: PayoutView) => void }) {
  const meta = payoutStatusMeta(payout.status);
  const actionLabel =
    payout.status === "pending_approval"
      ? "Review & approve"
      : payout.status === "approved"
      ? "Process payout"
      : payout.status === "reversed"
      ? "Review & re-approve"
      : "View record";
  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border bg-background-50 p-4 sm:flex-row sm:items-center sm:justify-between ${
        payout.status === "reversed" ? "border-danger-200" : "border-background-200"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div
          className={`flex h-10 w-10 flex-none items-center justify-center rounded-md ${
            payout.status === "reversed"
              ? "bg-danger-100 text-danger-600"
              : payout.isOrganizerReserved
              ? "bg-accent-100 text-accent-700"
              : "bg-secondary-100 text-secondary-700"
          }`}
        >
          <i className={`${payout.status === "reversed" ? "ri-close-circle-line" : payout.isOrganizerReserved ? "ri-flag-2-line" : "ri-bank-line"} text-lg`} />
        </div>
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground-900">{payout.groupName}</span>
            <span className="tnum text-xs text-foreground-400">{payout.groupCode}</span>
            <Badge color="secondary" dot>Cycle {payout.cycle}</Badge>
            {payout.isOrganizerReserved ? <Badge color="accent" dot>Reserved cycle</Badge> : null}
            <Badge color={meta.color} dot>{meta.label}</Badge>
          </div>
          <p className="mt-0.5 text-sm text-foreground-600">
            <span className="tnum font-medium">{payout.periodLabel}</span>
            <span className="mx-2 text-foreground-300">·</span>
            Winner {payout.winnerName} · {payout.winnerTicketNumber}
            <span className="mx-2 text-foreground-300">·</span>
            Net payable <CurrencyDisplay amount={payout.netPayable} hideSymbol />
            {payout.status === "reversed" ? (
              <span className="ml-2 text-danger-600">· Reversed: {payout.reversalReason}</span>
            ) : null}
          </p>
        </div>
      </div>
      <div className="flex flex-none items-center justify-between gap-4 sm:justify-end">
        <FundingCell payout={payout} />
        <div className="text-right">
          <p className="text-xs text-foreground-500">Prize</p>
          <p className="tnum text-base font-semibold text-foreground-900">
            <CurrencyDisplay amount={payout.prize} hideSymbol />
          </p>
        </div>
        <Button
          size="sm"
          variant={payout.status === "pending_approval" || payout.status === "reversed" ? "primary" : "outline"}
          icon={payout.status === "reversed" ? <i className="ri-restart-line" /> : <i className="ri-arrow-right-s-line" />}
          onClick={() => onOpen(payout)}
        >
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}

function Section({
  title,
  tone,
  count,
  payouts,
  onOpen,
  empty,
}: {
  title: string;
  tone: "default" | "danger" | "accent" | "secondary";
  count: number;
  payouts: PayoutView[];
  onOpen: (p: PayoutView) => void;
  empty: string;
}) {
  const dot =
    tone === "danger"
      ? "bg-danger-500"
      : tone === "accent"
      ? "bg-accent-500"
      : tone === "secondary"
      ? "bg-secondary-500"
      : "bg-primary-500";
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className={`h-2 w-2 rounded-full ${dot}`} />
        <h3 className="text-sm font-semibold text-foreground-800">{title}</h3>
        <span className="tnum rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-500">{count}</span>
      </div>
      {payouts.length === 0 ? (
        <p className="rounded-lg border border-dashed border-background-200 px-4 py-4 text-sm text-foreground-400">{empty}</p>
      ) : (
        <div className="space-y-2">
          {payouts.map((p) => (
            <PayoutEntry key={p.id} payout={p} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PayoutPage() {
  const navigate = useNavigate();
  const [payouts, setPayouts] = useState<PayoutView[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setPayouts(await payoutService.listPayouts());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load payouts.");
      setPayouts([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = useCallback((p: PayoutView) => navigate(`/payout/${p.id}`), [navigate]);

  const stats = useMemo(() => {
    const rows = payouts ?? [];
    const awaiting = rows.filter((p) => p.status === "pending_approval");
    const approved = rows.filter((p) => p.status === "approved");
    const pendingAmount = rows
      .filter((p) => p.status === "pending_approval" || p.status === "approved")
      .reduce((s, p) => s + p.netPayable, 0);
    const monthStart = startOfMonth();
    const disbursedThisMonth = rows
      .filter(
        (p) => p.status === "disbursed" && p.disbursements.some((d) => new Date(d.recordedAt) >= monthStart)
      )
      .reduce((s, p) => s + p.amountPaid, 0);
    return { awaiting: awaiting.length, approved: approved.length, pendingAmount, disbursedThisMonth };
  }, [payouts]);

  const grouped = useMemo(() => groupPayouts(payouts ?? []), [payouts]);

  if (error && !payouts) {
    return (
      <div>
        <PageHeader
          breadcrumb={
            <>
              <BreadcrumbItem label="Home" to="/" />
              <BreadcrumbItem label="Payout" isLast />
            </>
          }
          title="Payouts"
        />
        <Card>
          <ErrorState message={error} onRetry={load} />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Payout" isLast />
          </>
        }
        title="Payouts"
        subtitle="Who is owed the pool, and whether the cash is in hand"
        actions={
          <Button variant="outline" size="sm" icon={<i className="ri-file-list-3-line" />} onClick={() => navigate("/payout/history")}>
            Payout history
          </Button>
        }
      />

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!payouts ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-24 animate-pulse rounded-lg bg-background-200" />
            ))}
          </div>
          <div className="h-28 animate-pulse rounded-lg bg-background-200" />
          <div className="h-28 animate-pulse rounded-lg bg-background-200" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Awaiting approval"
              value={stats.awaiting}
              icon="ri-time-line"
              iconClass="bg-warning-100 text-warning-700"
              tone="warning"
            />
            <StatCard
              label="Approved · to disburse"
              value={stats.approved}
              icon="ri-check-double-line"
              iconClass="bg-info-100 text-info-800"
              tone="accent"
            />
            <StatCard
              label="Amount pending"
              value={<CurrencyDisplay amount={stats.pendingAmount} hideSymbol />}
              icon="ri-bank-line"
              iconClass="bg-primary-100 text-primary-700"
              tone="accent"
            />
            <StatCard
              label={`Disbursed · ${formatMonthYear(startOfMonth())}`}
              value={<CurrencyDisplay amount={stats.disbursedThisMonth} hideSymbol />}
              icon="ri-arrow-up-circle-line"
              iconClass="bg-success-100 text-success-600"
              tone="success"
            />
          </div>

          <Section
            title="Awaiting approval"
            tone="danger"
            count={grouped.awaiting.length}
            payouts={grouped.awaiting}
            onOpen={open}
            empty="No payouts waiting for approval. Clear."
          />
          <Section
            title="Approved & ready to disburse"
            tone="accent"
            count={grouped.approved.length}
            payouts={grouped.approved}
            onOpen={open}
            empty="Nothing approved and waiting to be paid out."
          />
          <Section
            title="Recently completed"
            tone="secondary"
            count={grouped.completed.length}
            payouts={grouped.completed}
            onOpen={open}
            empty="No completed payouts to show."
          />
        </div>
      )}
    </div>
  );
}